'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from './button'
import { cn } from '../lib/utils'

interface LegendItem {
  color: string
  label: string
  striped?: boolean
  dot?: boolean
}

interface LegendSection {
  title: string
  items: LegendItem[]
}

const WERKBON_ITEMS: LegendItem[] = [
  { color: '#E5E7EB', label: 'Gepland' },
  { color: '#BFDBFE', label: 'In uitvoering' },
  { color: '#FDE68A', label: 'Ingediend' },
  { color: '#BBF7D0', label: 'Goedgekeurd' },
  { color: '#FECACA', label: 'Afgekeurd' },
  { color: '#DDD6FE', label: 'Gefactureerd' },
]

const LEAVE_BASE_ITEMS: LegendItem[] = [
  { color: '#D1FAE5', label: 'Vakantie' },
  { color: '#99F6E4', label: 'Ziek' },
  { color: '#E5E7EB', label: 'Overig verlof' },
]

const LEAVE_PENDING_ITEM: LegendItem = {
  color: '#E5E7EB',
  label: 'Aangevraagd (nog niet goedgekeurd)',
  striped: true,
}

const MONTH_DOT_ITEMS: LegendItem[] = [
  { color: '#60A5FA', label: 'Werkbon op die dag', dot: true },
  { color: '#4ADE80', label: 'Verlof op die dag', dot: true },
]

/**
 * Ronde 4 W8 (spec 08 §5.1) — capaciteitskleur per medewerker-rij op het
 * planbord. De kleuren komen 1:1 uit de spec-tabel en sluiten aan op de
 * `bg-{green,amber,red,blue,gray}-50` die `getEmployeeRowBg(tone)` toepast.
 */
const CAPACITY_ITEMS: LegendItem[] = [
  { color: '#D1FAE5', label: 'Ruim beschikbaar (< 80%)' },
  { color: '#FDE68A', label: 'Bijna vol (80–110%)' },
  { color: '#FECACA', label: 'Overbelast (> 110%)' },
  { color: '#DBEAFE', label: 'Onderbezet (< 60%)' },
  { color: '#E5E7EB', label: 'Niet inplanbaar (verlof/ziek/geen contract)' },
]

interface PlanningLegendProps {
  showMonthDots?: boolean
  showPendingLeave?: boolean
  /**
   * Verberg het 'Gefactureerd' item. Handig in de portal waar de monteur geen
   * onderscheid hoeft te zien tussen goedgekeurd en gefactureerd werk.
   */
  hideInvoiced?: boolean
  /**
   * Ronde 4 W8 — toon de capaciteits-rijkleur-legenda (alleen op het
   * backoffice-planbord waar de medewerker-rij op bezetting gekleurd wordt).
   */
  showCapacity?: boolean
  className?: string
}

export function PlanningLegend({
  showMonthDots = false,
  showPendingLeave = false,
  hideInvoiced = false,
  showCapacity = false,
  className,
}: PlanningLegendProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const leaveItems: LegendItem[] = showPendingLeave
    ? [...LEAVE_BASE_ITEMS, LEAVE_PENDING_ITEM]
    : LEAVE_BASE_ITEMS

  const werkbonItems = hideInvoiced
    ? WERKBON_ITEMS.filter((it) => it.label !== 'Gefactureerd')
    : WERKBON_ITEMS

  const sections: LegendSection[] = [
    { title: 'Werkbonnen', items: werkbonItems },
    { title: 'Verlof', items: leaveItems },
    ...(showCapacity ? [{ title: 'Capaciteit (rijkleur)', items: CAPACITY_ITEMS }] : []),
    ...(showMonthDots ? [{ title: 'Maandview', items: MONTH_DOT_ITEMS }] : []),
  ]

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="mr-2 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v.01M12 11v5" />
        </svg>
        Legenda
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label="Kleuren-legenda"
          className="absolute right-0 z-50 mt-2 w-72 max-w-[calc(100vw-1rem)] rounded-lg border bg-white p-3 shadow-lg"
        >
          {sections.map((section, idx) => (
            <div key={section.title} className={idx > 0 ? 'mt-3 border-t pt-3' : ''}>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-brand-gray-mid">
                {section.title}
              </h4>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.label} className="flex items-center gap-2 text-sm text-brand-gray-dark">
                    <LegendSwatch item={item} />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function LegendSwatch({ item }: { item: LegendItem }) {
  if (item.dot) {
    return (
      <span
        aria-hidden="true"
        className="inline-block h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: item.color }}
      />
    )
  }
  const style: React.CSSProperties = item.striped
    ? {
        backgroundColor: item.color,
        backgroundImage:
          'repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 4px, transparent 4px 8px)',
      }
    : { backgroundColor: item.color }
  return (
    <span
      aria-hidden="true"
      className="inline-block h-4 w-4 shrink-0 rounded border border-brand-gray-mid/30"
      style={style}
    />
  )
}
